import { FunctionComponent, useCallback, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Modal } from '@payaca/ambrosia';
import * as proposalsActions from '@payaca/store/proposals/proposalsActions';

import FeedbackBlock from '@payaca/components/feedbackBlock/FeedbackBlock';
import { FeedbackLevel } from '@payaca/types/feedbackTypes';
import UntitledIcon from '@payaca/untitled-icons';

type Props = {
  isOpen: boolean;
  changeProposalId: number;
  onClose: () => void;
  markAsSentCallback?: () => void;
};

const MarkChangeProposalAsSentModal: FunctionComponent<Props> = ({
  isOpen,
  changeProposalId,
  onClose,
  markAsSentCallback,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [errorMessages, setErrorMessages] = useState<string[]>();

  const [isProcessing, setIsProcessing] = useState(false);

  const onMarkAsSent = useCallback(() => {
    setIsProcessing(true);
    dispatch(
      proposalsActions.markChangeProposalAsSent.request({
        changeProposalId: changeProposalId,
        callback: () => {
          setIsProcessing(false);
          markAsSentCallback?.();
          setErrorMessages([]);
          onClose();
        },
        onErrorCallback: (errorMessages: string[]) => {
          setErrorMessages(errorMessages);
          setIsProcessing(false);
        },
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [changeProposalId, markAsSentCallback, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      title="Mark change proposal as sent"
      onClose={onClose}
    >
      <Modal.Body>
        <p>
          This option is intended to be used in situations where you have sent
          the change proposal offline. For example - where you have printed off
          the change proposal and handed it to your customer on paper, in
          person.
        </p>
        <p>When an change proposal has been marked as sent:</p>
        <ul>
          <li>No emails or other communication will go to the customer</li>
          <li>
            You will not be able to retrospectively send this change proposal to
            the customer using the standard Payaca process
          </li>
          <li>
            If your account is connected to any third party services (Zapier,
            Xero, Quickbooks), they will treat this change proposal as they
            would any other sent change proposal
          </li>
        </ul>
        {!!errorMessages?.length && (
          <FeedbackBlock feedbackLevel={FeedbackLevel.ERROR}>
            <strong>This change proposal could not be marked as sent:</strong>
            <ul>
              {errorMessages.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </FeedbackBlock>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            onClick={onMarkAsSent}
            isProcessing={isProcessing}
            replaceContentWhenProcessing
          >
            Mark as sent without sending
            <UntitledIcon name="chevron-right" />
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default MarkChangeProposalAsSentModal;
